import { useState } from "react"
import { useTranslation } from "react-i18next"
import { ChatModal } from "../ChatModal"
import { GroupName } from "./GroupName"
import { PeoplePicker, type Colleague } from "./PeoplePicker"

type NewChatProps = {
  colleagues: Colleague[]
  /** One person chosen: straight to a conversation, no name needed. */
  onStartDirect: (person: Colleague) => void
  onCreateGroup: (name: string, members: Colleague[]) => void
  onClose: () => void
  isCreating?: boolean
}

/**
 * Two steps in one modal: choose people, then name the group. Choosing one
 * person skips the second step, because a direct conversation has no name of
 * its own — it is called after whoever is in it.
 */
export function NewChat({
  colleagues,
  onStartDirect,
  onCreateGroup,
  onClose,
  isCreating,
}: NewChatProps) {
  const { t } = useTranslation()
  const [members, setMembers] = useState<Colleague[]>([])
  const [isNaming, setIsNaming] = useState(false)

  return (
    <ChatModal
      ariaLabel={String(t("chat.NEW_CHAT"))}
      onClose={onClose}
      isCompact={isNaming}
    >
      {isNaming ? (
        <GroupName
          members={members}
          onBack={() => setIsNaming(false)}
          isCreating={isCreating}
          onCreate={(name) => onCreateGroup(name, members)}
        />
      ) : (
        <PeoplePicker
          colleagues={colleagues}
          initialChosen={members}
          onConfirm={(chosen) => {
            const [only] = chosen
            if (chosen.length === 1 && only) return onStartDirect(only)

            setMembers(chosen)
            setIsNaming(true)
          }}
        />
      )}
    </ChatModal>
  )
}
